// react
import { useMemo, useState } from 'react';

// types
import { Invoice, Pagination } from '../../../../@types';
import { invoiceStatuses } from '../../../../@types/unions';

// i18next
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Grid } from '@mui/material';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardInvoice } from '../../../cards/CardInvoice/CardInvoice';

// ui
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';

// utils
import { translateStatus } from '../../../../utils/translations';

export interface TableInvoicesProps {
	invoices: Invoice[];
	isLoading?: boolean;
	pagination: Pagination;
	refetchInvoices?: () => void;
}

export const TableInvoices = ({
	invoices,
	isLoading,
	pagination,
	refetchInvoices,
}: TableInvoicesProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	const paramStatus = searchParams.get('status');

	const defaultFiltersValues = {
		status: 'all',
	};

	const [filtersValues, setFiltersValues] = useState({
		status: paramStatus || defaultFiltersValues.status,
	});

	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;

		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	const idFormFilters = 'form-invoices-filters';

	const optionsStatus = invoiceStatuses.map((status) => ({
		label: translateStatus(status, t),
		value: status,
	}));

	const filters: FilterProps[] = [
		{
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-status`,
			label: t('status'),
			name: 'status',
			onChange: handleChangeFilterValue,
			options: optionsStatus,
			type: 'SELECT',
			value: filtersValues.status,
		},
	];

	const hasFilters = filters.length > 0;

	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 2,
			},
			isSortable: true,
			label: t('invoice'),
			value: 'num',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('supplier'),
			value: 'supplier',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('status'),
			value: 'status',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('contract'),
			value: 'contract',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('amount'),
			value: 'amount',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('tax'),
			value: 'tax',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('total'),
			value: 'total',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('updated'),
			value: 'modified',
		},
	];

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: { children: t('empty.invoices.content') },
			IconCircleProps: { color: 'grey', type: 'invoice' },
			title: { children: t('empty.invoices.title') },
		};

		return emptyProps;
	}, [t]);

	const content = useMemo(() => {
		if (isLoading || !invoices.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{invoices.map((invoice, i) => {
					return (
						<Grid item key={`invoice-${invoice.id}-${i}`} xs={12}>
							<CardInvoice invoice={invoice} refetchInvoices={refetchInvoices} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, invoices, isLoading, refetchInvoices]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} md={hasFilters ? true : 12}>
				<FormSearch InputProps={{ placeholder: t('searchById') }} />
			</Grid>
			{hasFilters && (
				<Grid item xs={6} md="auto">
					<BtnFilters
						filters={filters}
						onReset={() => {
							setFiltersValues(defaultFiltersValues);
							searchParams.delete('status');
							setSearchParams(searchParams);
						}}
					/>
				</Grid>
			)}
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={invoices.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
